import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseCsvDateToIso, parseCsvDateToIsoOptional } from "../shared/utils/csvDate";
import { parseRequiredFiniteNumber } from "../shared/utils/csvNumber";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile, downloadCsvTextFromString, escapeCsvCell } from "../shared/utils/csvFileHelpers";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { formatDateCell, toDateInputValueUtcSlice } from "../shared/utils/dateDisplay";
import { formatAmountCell } from "../shared/utils/financeDisplay";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { useListPagination } from "../shared/hooks/useListPagination";
import ListPaginationBar from "../components/ListPaginationBar";
import FinancePageScaffold from "../components/finance/FinancePageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import FinanceSectionTabs, { FINANCE_TAB_TRANSACTION } from "../components/FinanceSectionTabs";
import FinanceImportCsvPanel from "../components/finance/FinanceImportCsvPanel";
import FinanceListToolbar from "../components/finance/FinanceListToolbar";
import FinanceSummaryStatCards from "../components/finance/FinanceSummaryStatCards";
import BulkUpdateTransactionsModal from "../components/finance/BulkUpdateTransactionsModal";
import ListFiltersSlideOver from "../components/entity/ListFiltersSlideOver";
import ListFiltersToolbarButton from "../components/entity/ListFiltersToolbarButton";
import SplitTransactionModal from "../components/finance/SplitTransactionModal";
import { useAdvancedFilter } from "../shared/hooks/useAdvancedFilter";
import {
  advancedFilterAppliedToFormValues,
  patchAdvancedFilterCriteria,
} from "../shared/utils/advancedFilter";
import { filterTransactionsByAdvancedCriteria } from "../shared/utils/transactionAdvancedFilter";
import { getTransactionAdvancedFilterFieldDefs } from "../shared/utils/transactionAdvancedFilterFields";
import SearchableSelect from "../components/SearchableSelect";
import { bulkUpdateTransactions } from "../shared/api/transactions";
import { isValidYyyyMmDd, toYmdFromDateLike } from "../shared/utils/calendarYmd";
import { summarizeIncomeExpenseRows } from "../shared/utils/financeDisplay";
import { IconEdit, IconDelete, IconSplit, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

const SORT_KEY_TYPES = { date: "date", amount: "number" };

/** API uses `name`; legacy DB rows may still have `description` until migration runs. */
function normalizeTransactionRow(t) {
  if (!t || typeof t !== "object") return t;
  const nameFromName = t.name != null ? String(t.name).trim() : "";
  const nameFromDesc = t.description != null ? String(t.description).trim() : "";
  const name = nameFromName || nameFromDesc;
  const { description: _legacyDescription, ...rest } = t;
  return { ...rest, name: name || "" };
}

function Transaction() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentObjs, setCurrentObjs] = useState([]);
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: "date", direction: "desc" }, initialViewMode: "table" });

  const transactionFilterFields = useMemo(
    () =>
      getTransactionAdvancedFilterFieldDefs({
        financialAccounts,
        assets,
        vendors,
      }),
    [financialAccounts, assets, vendors]
  );

  const {
    open: advancedFilterOpen,
    setOpen: setAdvancedFilterOpen,
    applied: advancedFilterApplied,
    setApplied: setAdvancedFilterApplied,
    activeCount: advancedFilterActiveCount,
    activeSummary: advancedFilterActiveSummary,
    clearApplied: clearAdvancedFilterApplied,
  } = useAdvancedFilter({ fields: transactionFilterFields });

  const transactionFilterValues = useMemo(
    () => advancedFilterAppliedToFormValues(advancedFilterApplied, transactionFilterFields),
    [advancedFilterApplied, transactionFilterFields]
  );

  const handleTransactionFilterFieldChange = useCallback(
    (fieldId, value) => {
      setAdvancedFilterApplied((prev) => patchAdvancedFilterCriteria(prev, fieldId, value));
    },
    [setAdvancedFilterApplied]
  );

  const [detailTransactionId, setDetailTransactionId] = useState(null);
  const [detailMode, setDetailMode] = useState("");
  const [detailDate, setDetailDate] = useState("");
  const [detailName, setDetailName] = useState("");
  const [detailAmount, setDetailAmount] = useState("");
  const [detailCategory, setDetailCategory] = useState("");
  const [detailTaxCategory, setDetailTaxCategory] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [detailFinancialAccountId, setDetailFinancialAccountId] = useState("");
  const [detailAssetId, setDetailAssetId] = useState("");
  const [detailVendorId, setDetailVendorId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getDocument();
    getLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listTransactions", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) =>
        setCurrentObjs((Array.isArray(data) ? data : []).map(normalizeTransactionRow))
      )
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function getLookups() {
    if (!account_id) return;
    Promise.all([
      apiFetch(serverUrl + "/listFinancialAccounts", {
        method: "POST",
        body: JSON.stringify({ account_id }),
      }).then((r) => r.json()),
      apiFetch(serverUrl + "/listAssets", {
        method: "POST",
        body: JSON.stringify({ account_id }),
      }).then((r) => r.json()),
      apiFetch(serverUrl + "/listVendors", {
        method: "POST",
        body: JSON.stringify({ account_id }),
      }).then((r) => r.json()),
    ])
      .then(([accounts, assetsList, vendorList]) => {
        setFinancialAccounts(Array.isArray(accounts) ? accounts : []);
        setAssets(Array.isArray(assetsList) ? assetsList : []);
        setVendors(Array.isArray(vendorList) ? vendorList : []);
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  const financialAccountById = {};
  const assetById = {};
  const vendorById = {};
  (financialAccounts || []).forEach((x) => {
    financialAccountById[x._id] = x;
  });
  (assets || []).forEach((x) => {
    assetById[x._id] = x;
  });
  (vendors || []).forEach((x) => {
    vendorById[x._id] = x;
  });

  const drilldownActive = searchParams.get("reportDrilldown") === "1";
  const drilldownStartDate = searchParams.get("startDate") || "";
  const drilldownEndDate = searchParams.get("endDate") || "";
  const drilldownAmountType = searchParams.get("amountType") || "";
  const drilldownBucketType = searchParams.get("bucketType") || "";
  const drilldownBucketLabel = searchParams.get("bucketLabel") || "";

  const hasValidDrilldownRange = isValidYyyyMmDd(drilldownStartDate) && isValidYyyyMmDd(drilldownEndDate) && drilldownStartDate <= drilldownEndDate;
  const hasValidDrilldownAmountType = drilldownAmountType === "income" || drilldownAmountType === "expense";
  const applyDrilldownFilter = drilldownActive && hasValidDrilldownRange && hasValidDrilldownAmountType;

  const rangeError = useMemo(() => {
    if (startDate && !isValidYyyyMmDd(startDate)) return "Start date must use YYYY-MM-DD format.";
    if (endDate && !isValidYyyyMmDd(endDate)) return "End date must use YYYY-MM-DD format.";
    if (startDate && endDate && startDate > endDate) return "Start date cannot be after end date.";
    return "";
  }, [startDate, endDate]);

  useEffect(() => {
    const list = currentObjs || [];
    const dateList = list
      .map((t) => toYmdFromDateLike(t?.date))
      .filter(Boolean)
      .sort();
    if (applyDrilldownFilter) {
      setStartDate(drilldownStartDate);
      setEndDate(drilldownEndDate);
      return;
    }
    if (dateList.length > 0) {
      setStartDate((prev) => prev || dateList[0]);
      setEndDate((prev) => prev || dateList[dateList.length - 1]);
    }
  }, [currentObjs, applyDrilldownFilter, drilldownStartDate, drilldownEndDate]);

  function getFilteredAndSortedData() {
    if (rangeError) return [];
    const searchLower = (searchTerm || "").toLowerCase().trim();
    let filtered = (currentObjs || []).filter((obj) => {
      const ymd = toYmdFromDateLike(obj?.date);
      if (!ymd) return false;
      if (startDate && ymd < startDate) return false;
      if (endDate && ymd > endDate) return false;
      if (applyDrilldownFilter) {
        const amountNum = Number(obj?.amount);
        if (!Number.isFinite(amountNum)) return false;
        if (drilldownAmountType === "income" && amountNum < 0) return false;
        if (drilldownAmountType === "expense" && amountNum >= 0) return false;
      }
      return true;
    });
    filtered = filterTransactionsByAdvancedCriteria(filtered, advancedFilterApplied);
    if (searchLower) {
      filtered = filtered.filter((obj) => {
        const accountName = obj.financialAccountId ? financialAccountById[obj.financialAccountId]?.name || "" : "";
        const assetName = obj.assetId ? assetById[obj.assetId]?.name || "" : "";
        const vendorName = obj.vendorId ? vendorById[obj.vendorId]?.name || "" : "";
        return (
          (obj.name && String(obj.name).toLowerCase().includes(searchLower)) ||
          (obj.category && String(obj.category).toLowerCase().includes(searchLower)) ||
          (obj.taxCategory && String(obj.taxCategory).toLowerCase().includes(searchLower)) ||
          (obj.notes && String(obj.notes).toLowerCase().includes(searchLower)) ||
          String(obj.amount ?? "").toLowerCase().includes(searchLower) ||
          accountName.toLowerCase().includes(searchLower) ||
          assetName.toLowerCase().includes(searchLower) ||
          vendorName.toLowerCase().includes(searchLower)
        );
      });
    }
    return sortRowsByConfig(filtered, sortConfig, SORT_KEY_TYPES);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function parseRecordsFromCsvText(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const dateIdx = findCsvColumnIndex(headers, ["date", "transaction_date"]);
    const nameIdx = findCsvColumnIndex(headers, ["name", "description"]);
    const amountIdx = findCsvColumnIndex(headers, ["amount"]);
    const categoryIdx = findCsvColumnIndex(headers, ["category"]);
    const taxCategoryIdx = findCsvColumnIndex(headers, ["tax_category", "taxcategory"]);
    const notesIdx = findCsvColumnIndex(headers, ["notes"]);
    const financialAccountIdIdx = findCsvColumnIndex(headers, [
      "financialaccountid",
      "financial_account_id",
      "financial_account",
    ]);
    const assetIdIdx = findCsvColumnIndex(headers, ["assetid", "asset_id", "asset"]);

    if (dateIdx === -1) throw new Error("CSV must contain a date column");
    if (nameIdx === -1) throw new Error("CSV must contain a name column (header: name, or legacy: description)");
    if (amountIdx === -1) throw new Error("CSV must contain an amount column");

    return rows.map((values) => ({
      date: values[dateIdx] ?? "",
      name: values[nameIdx] ?? "",
      amount: values[amountIdx] ?? "",
      category: categoryIdx >= 0 && values[categoryIdx] != null ? values[categoryIdx] : "",
      taxCategory: taxCategoryIdx >= 0 && values[taxCategoryIdx] != null ? values[taxCategoryIdx] : "",
      notes: notesIdx >= 0 && values[notesIdx] != null ? values[notesIdx] : "",
      financialAccountId:
        financialAccountIdIdx >= 0 && values[financialAccountIdIdx] != null ? values[financialAccountIdIdx] : "",
      assetId: assetIdIdx >= 0 && values[assetIdIdx] != null ? values[assetIdIdx] : "",
    }));
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseRecordsFromCsvText(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const dateParsed = parseCsvDateToIsoOptional(record?.date);
        const rowName = record?.name != null ? String(record.name).trim() : "";
        const amountParsed = parseRequiredFiniteNumber(record?.amount, { fieldName: "Amount" });

        if (!dateParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: date: ${dateParsed.error}`);
          continue;
        }
        if (!dateParsed.iso) {
          errorCount++;
          errors.push(`Row ${i + 2}: Date is required`);
          continue;
        }
        if (!rowName) {
          errorCount++;
          errors.push(`Row ${i + 2}: Name is required`);
          continue;
        }
        if (!amountParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${amountParsed.error}`);
          continue;
        }

        try {
          const response = await apiFetch(serverUrl + "/createTransaction", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              date: dateParsed.iso,
              name: rowName,
              amount: amountParsed.value,
              category: record?.category || "",
              taxCategory: record?.taxCategory || "",
              notes: record?.notes || "",
              financialAccountId: record?.financialAccountId || "",
              assetId: record?.assetId || "",
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }

      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
      });
      getDocument();
      getLookups();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("transactions-sample.csv", [
      "date,name,amount,category,taxCategory,notes,financialAccountId,assetId",
      "2026-03-01,Office supplies,125.50,Operations,Input Tax,Printer paper and ink,,",
      '2026-03-05,"Laptop purchase",1450.00,Equipment,Capital,Dev machine,',
      "2026-03-12,Rent payment,2500.00,Facility,Non Taxable,March rent,,",
    ]);
  }

  function exportSelectedTransactionsCsv() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one transaction to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const idSet = selectedVisibleIds;
      const rows = (currentObjs || [])
        .filter((t) => t && idSet.has(String(t._id)))
        .sort((a, b) => {
          const da = toYmdFromDateLike(a?.date);
          const db = toYmdFromDateLike(b?.date);
          if (da !== db) return db.localeCompare(da);
          return String(a?._id ?? "").localeCompare(String(b?._id ?? ""));
        });
      if (rows.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching transactions found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const header = "date,name,amount,category,taxCategory,notes,financialAccount,asset,vendor";
      const lines = rows.map((t) => {
        const ymd = toYmdFromDateLike(t?.date) || "";
        const amt = t?.amount;
        const amountStr =
          amt != null && Number.isFinite(Number(amt)) ? String(Number(amt)) : "";
        const financialAccountName = t?.financialAccountId ? financialAccountById[t.financialAccountId]?.name || "" : "";
        const assetName = t?.assetId ? assetById[t.assetId]?.name || "" : "";
        const vendorName = t?.vendorId ? vendorById[t.vendorId]?.name || "" : "";
        return [
          escapeCsvCell(ymd),
          escapeCsvCell(t?.name ?? ""),
          escapeCsvCell(amountStr),
          escapeCsvCell(t?.category ?? ""),
          escapeCsvCell(t?.taxCategory ?? ""),
          escapeCsvCell(t?.notes ?? ""),
          escapeCsvCell(financialAccountName),
          escapeCsvCell(assetName),
          escapeCsvCell(vendorName),
        ].join(",");
      });
      const csv = [header, ...lines].join("\r\n");
      downloadCsvTextFromString("transactions-export.csv", csv);
      setBannerError("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  function openDetail(obj, mode = "view") {
    setDetailMode(mode);
    if (!obj && mode === "new") {
      setDetailTransactionId(null);
      setDetailDate("");
      setDetailName("");
      setDetailAmount("");
      setDetailCategory("");
      setDetailTaxCategory("");
      setDetailNotes("");
      setDetailFinancialAccountId("");
      setDetailAssetId("");
      setDetailVendorId("");
      return;
    }
    if (!obj) {
      setDetailMode("");
      return;
    }
    setDetailTransactionId(obj._id);
    setDetailDate(toDateInputValueUtcSlice(obj.date));
    setDetailName(obj.name || "");
    setDetailAmount(obj.amount != null ? String(obj.amount) : "");
    setDetailCategory(obj.category || "");
    setDetailTaxCategory(obj.taxCategory || "");
    setDetailNotes(obj.notes || "");
    setDetailFinancialAccountId(obj.financialAccountId || "");
    setDetailAssetId(obj.assetId || "");
    setDetailVendorId(obj.vendorId || "");
  }

  async function persistDetailChanges() {
    const dateParsed = parseCsvDateToIso(detailDate || "");
    if (!dateParsed.ok) {
      setBannerError(`Date: ${dateParsed.error}`);
      return;
    }
    const nameVal = (detailName || "").trim();
    if (!nameVal) {
      setBannerError("Name is required");
      return;
    }
    const amountParsed = parseRequiredFiniteNumber(detailAmount, { fieldName: "Amount" });
    if (!amountParsed.ok) {
      setBannerError(amountParsed.error);
      return;
    }

    const payload = {
      account_id,
      date: dateParsed.iso,
      name: nameVal,
      amount: amountParsed.value,
      category: (detailCategory || "").trim(),
      taxCategory: (detailTaxCategory || "").trim(),
      notes: (detailNotes || "").trim(),
      financialAccountId: detailFinancialAccountId || "",
      assetId: detailAssetId || "",
      vendorId: detailVendorId || "",
    };

    const isNew = detailMode === "new" || !detailTransactionId;
    const url = isNew ? serverUrl + "/createTransaction" : serverUrl + "/updateTransaction";
    const body = isNew ? payload : { ...payload, _id: detailTransactionId };

    try {
      const resp = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const text = await resp.text();
      if (!resp.ok) {
        setBannerError(text || "Request failed.");
        return;
      }
      setDetailMode("");
      getDocument();
      getLookups();
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function deleteDocument(currentObj) {
    const desc = currentObj?.name ? String(currentObj.name) : "this transaction";
    setConfirmDialog({
      open: true,
      title: "Delete transaction",
      mode: "confirm",
      message: `Are you sure you want to delete ${desc}?`,
      onConfirm: () => {
        apiFetch(serverUrl + "/deleteTransaction", {
          method: "POST",
          body: JSON.stringify({ account_id, _id: currentObj?._id }),
        })
          .then(async (response) => {
            const data = await response.text();
            if (!response.ok) throw new Error(data || "Failed to delete transaction");
            const newSelectedIds = new Set(selectedIds);
            newSelectedIds.delete(currentObj?._id);
            setSelectedIds(newSelectedIds);
            getDocument();
          })
          .catch((err) => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete transactions", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete transactions",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteTransaction", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  function openBulkUpdate() {
    if (getSelectedVisibleIds().size === 0) {
      setConfirmDialog({
        open: true,
        title: "Bulk update",
        message: "Please select at least one transaction to update",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkUpdateOpen(true);
  }

  function openSplit(currentObj) {
    setSplitTarget(currentObj || null);
    setSplitOpen(true);
  }

  async function applyBulkUpdate(fields) {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setBulkUpdateOpen(false);
      return;
    }
    const ids = Array.from(selectedVisibleIds);
    const payload = { account_id, _ids: ids, ...fields };
    try {
      setBulkUpdating(true);
      const res = await bulkUpdateTransactions(payload);
      const matched = res && typeof res.matched === "number" ? res.matched : ids.length;
      const modified = res && typeof res.modified === "number" ? res.modified : 0;
      setConfirmDialog({
        open: true,
        title: "Bulk update completed",
        mode: "alert",
        onConfirm: null,
        message: `Updated ${modified} transaction(s) (matched ${matched}).`,
      });
      setSelectedIds(new Set());
      setBulkUpdateOpen(false);
      getDocument();
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: "Bulk update failed",
        mode: "alert",
        onConfirm: null,
        message: err && err.message ? err.message : "Bulk update failed",
      });
    } finally {
      setBulkUpdating(false);
    }
  }

  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const listSummary = summarizeIncomeExpenseRows(filteredRows);
  const {
    pageItems: paginatedRows,
    paginationBarProps,
    getSelectAllCheckboxProps,
  } = useListPagination(filteredRows, [
    searchTerm,
    startDate,
    endDate,
    sortConfig.key,
    sortConfig.direction,
    applyDrilldownFilter,
    drilldownAmountType,
    rangeError,
    advancedFilterApplied,
  ]);

  return (
    <FinancePageScaffold
      title="Financial Transaction"
      titleTabs={<FinanceSectionTabs activeTab={FINANCE_TAB_TRANSACTION} />}
      subtitle="Track financial transactions with account and asset linkage."
      bannerError={rangeError || bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          <SplitTransactionModal
            open={splitOpen}
            accountId={account_id}
            transaction={splitTarget}
            onClose={() => {
              setSplitOpen(false);
              setSplitTarget(null);
            }}
            onSaved={() => {
              getDocument();
              getLookups();
            }}
          />

          <BulkUpdateTransactionsModal
            open={bulkUpdateOpen}
            onClose={() => {
              if (bulkUpdating) return;
              setBulkUpdateOpen(false);
            }}
            selectedCount={selectedVisibleCount}
            assets={assets || []}
            loading={bulkUpdating}
            onApply={applyBulkUpdate}
          />

          <ListFiltersSlideOver
            open={advancedFilterOpen}
            onClose={() => setAdvancedFilterOpen(false)}
            subtitle="Name, category, and related criteria."
            fields={transactionFilterFields}
            values={transactionFilterValues}
            onFieldChange={handleTransactionFilterFieldChange}
            onClear={clearAdvancedFilterApplied}
          />

          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => setDetailMode("")}
                onKeyDown={(e) => e.key === "Escape" && setDetailMode("")}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel">
                <div
                  className="panel-row"
                  style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}
                >
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New transaction" : "Transaction details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button type="button" className="btn btn-sm btn-primary" onClick={persistDetailChanges}>
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      onClick={() => setDetailMode("")}
                      style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Date *</label>
                      <input type="date" value={detailDate} onChange={(e) => setDetailDate(e.target.value)} className="input input-full" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Amount *</label>
                      <input type="number" step="0.01" value={detailAmount} onChange={(e) => setDetailAmount(e.target.value)} className="input input-full" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gridColumn: "1 / -1" }}>
                      <label className="panel-label">Name *</label>
                      <input type="text" value={detailName} onChange={(e) => setDetailName(e.target.value)} className="input input-full" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Category</label>
                      <input type="text" value={detailCategory} onChange={(e) => setDetailCategory(e.target.value)} className="input input-full" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Tax Category</label>
                      <input type="text" value={detailTaxCategory} onChange={(e) => setDetailTaxCategory(e.target.value)} className="input input-full" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Financial Account</label>
                      <SearchableSelect
                        value={detailFinancialAccountId || ""}
                        onChange={(v) => setDetailFinancialAccountId(v)}
                        options={(financialAccounts || []).map((a) => ({ value: a._id, label: a.name }))}
                        maxVisibleItems={4}
                        dropdownPortal={true}
                        title="Financial Account"
                        placeholder="Select account"
                        searchPlaceholder="Search account…"
                        allowEmpty={true}
                        emptyLabel="— None"
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Asset</label>
                      <SearchableSelect
                        value={detailAssetId || ""}
                        onChange={(v) => setDetailAssetId(v)}
                        options={(assets || []).map((a) => ({ value: a._id, label: a.name }))}
                        maxVisibleItems={4}
                        dropdownPortal={true}
                        title="Asset"
                        placeholder="Select asset"
                        searchPlaceholder="Search asset…"
                        allowEmpty={true}
                        emptyLabel="— None"
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Vendor</label>
                      <SearchableSelect
                        value={detailVendorId || ""}
                        onChange={(v) => setDetailVendorId(v)}
                        options={(vendors || []).map((v) => ({
                          value: String(v._id),
                          label: `${v.name || "Vendor"}${v.type ? ` (${v.type})` : ""}`,
                          keywords: [v.name, v.type, v.phone, v.email].filter(Boolean).join(" "),
                        }))}
                        maxVisibleItems={4}
                        dropdownPortal={true}
                        title="Vendor"
                        placeholder="Select vendor"
                        searchPlaceholder="Search vendor…"
                        allowEmpty={true}
                        emptyLabel="— None"
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gridColumn: "1 / -1" }}>
                      <label className="panel-label">Notes</label>
                      <textarea value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)} rows={3} className="input input-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <FinanceImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="transactions-csv-import"
            importing={importing}
            columnHint="Columns: date (required), name (required; legacy CSV header: description), amount (required). Optional: category, taxCategory, notes, financialAccountId, assetId"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />

          <div className="toolbar">
            <span className="toolbar-label">Start Date:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
              aria-label="Start date"
            />
            <span className="toolbar-label">End Date:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
              aria-label="End date"
            />
          </div>

          <FinanceListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, amount, category, account, asset, vendor…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <ListFiltersToolbarButton
                onClick={() => setAdvancedFilterOpen(true)}
                activeCount={advancedFilterActiveCount}
                activeSummary={advancedFilterActiveSummary}
              />
            }
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            extraActions={
              <>
                <button
                  type="button"
                  className="btn btn-md btn-primary-outline"
                  disabled={selectedVisibleCount === 0 || exporting}
                  onClick={() => exportSelectedTransactionsCsv()}
                >
                  {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
                </button>
                <button
                  type="button"
                  onClick={openBulkUpdate}
                  disabled={selectedVisibleCount === 0 || bulkUpdating}
                  className="btn btn-md btn-muted"
                >
                  Update{selectedCountSuffix}
                </button>
              </>
            }
            onNew={() => {
              openDetail(null, "new");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          {applyDrilldownFilter && (
            <div className="panel" style={{ marginBottom: 8 }}>
              <div className="panel-row" style={{ justifyContent: "space-between" }}>
                <span className="panel-hint-inline">
                  Drilldown filter active: {drilldownAmountType === "income" ? "Income" : "Expense"} • {drilldownBucketType || "bucket"} • {drilldownBucketLabel || `${drilldownStartDate} to ${drilldownEndDate}`}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setSearchParams({});
                    const dateList = (currentObjs || [])
                      .map((t) => toYmdFromDateLike(t?.date))
                      .filter(Boolean)
                      .sort();
                    if (dateList.length > 0) {
                      setStartDate(dateList[0]);
                      setEndDate(dateList[dateList.length - 1]);
                    }
                  }}
                >
                  Clear drilldown
                </button>
              </div>
            </div>
          )}
        </>
      }
      list={
        <div className="table-scroll">
          <FinanceSummaryStatCards
            totalIncome={listSummary.totalIncome}
            totalExpense={listSummary.totalExpense}
            netCashFlow={listSummary.netCashFlow}
          />
          <ListPaginationBar {...paginationBarProps} />
          {viewMode === "table" && (
            <div className="table-wrapper table-wrapper-finance-transactions">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                      />
                    </th>
                    <th className="sortable" onClick={() => handleSort("date")}>
                      Date
                    </th>
                    <th className="sortable" onClick={() => handleSort("name")}>
                      Name
                    </th>
                    <th className="sortable" onClick={() => handleSort("amount")}>
                      Amount
                    </th>
                    <th className="sortable" onClick={() => handleSort("category")}>
                      Category
                    </th>
                    <th className="sortable" onClick={() => handleSort("taxCategory")}>
                      Tax Category
                    </th>
                    <th>Financial Account</th>
                    <th>Asset</th>
                    <th>Vendor</th>
                    <th>Notes</th>
                    <TableActionColumnHeader />
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((currentObj) => (
                    <tr key={currentObj._id}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(currentObj._id)}
                          onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)}
                        />
                      </td>
                      <td>{formatDateCell(currentObj.date)}</td>
                      <td className="table-cell-finance-desc">
                        <button
                          type="button"
                          className="btn-link block"
                          onClick={() => openDetail(currentObj, "view")}
                          title={
                            currentObj.name != null && String(currentObj.name).trim()
                              ? String(currentObj.name).trim()
                              : undefined
                          }
                        >
                          {currentObj.name || "—"}
                        </button>
                      </td>
                      <td>{formatAmountCell(currentObj.amount)}</td>
                      <td>{currentObj.category || "—"}</td>
                      <td>{currentObj.taxCategory || "—"}</td>
                      <td>{currentObj.financialAccountId ? financialAccountById[currentObj.financialAccountId]?.name || "—" : "—"}</td>
                      <td>{currentObj.assetId ? assetById[currentObj.assetId]?.name || "—" : "—"}</td>
                      <td>{currentObj.vendorId ? vendorById[currentObj.vendorId]?.name || "—" : "—"}</td>
                      <td className="table-cell-notes">{currentObj.notes || "—"}</td>
                      <td className="text-center">
                        <RowActionIcons>
                          <button
                            type="button"
                            onClick={() => openSplit(currentObj)}
                            className="btn-link"
                            style={iconBtnStyle}
                            title="Split (NLP)"
                            aria-label="Split transaction (NLP)"
                          >
                            <IconSplit size={18} />
                          </button>
                          <button type="button" onClick={() => openDetail(currentObj, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                            <IconEdit size={18} />
                          </button>
                          <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                            <IconDelete size={18} />
                          </button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {paginatedRows.map((t) => (
                <div key={t._id} className="card">
                  <div className="card-header">
                    <input type="checkbox" checked={selectedIds.has(t._id)} onChange={(e) => handleSelectOne(t._id, e.target.checked)} />
                    <div className="card-header-content">
                      <button
                        type="button"
                        className="btn-link card-title finance-transaction-card-title"
                        onClick={() => openDetail(t, "view")}
                        title={t.name != null && String(t.name).trim() ? String(t.name).trim() : undefined}
                      >
                        {t.name || "—"}
                      </button>
                      <div className="panel-hint">{[formatDateCell(t.date), formatAmountCell(t.amount)].filter(Boolean).join(" • ")}</div>
                    </div>
                  </div>
                  <div className="card-meta-muted">
                    Category: {t.category || "—"} | Tax: {t.taxCategory || "—"}
                  </div>
                  <div className="card-meta-muted">Account: {t.financialAccountId ? financialAccountById[t.financialAccountId]?.name || "—" : "—"}</div>
                  <div className="card-meta-muted">Asset: {t.assetId ? assetById[t.assetId]?.name || "—" : "—"}</div>
                  <div className="card-meta-muted">Vendor: {t.vendorId ? vendorById[t.vendorId]?.name || "—" : "—"}</div>
                  {t.notes ? <div className="panel-hint card-notes">{t.notes}</div> : <div className="card-meta-muted">—</div>}
                  <div className="card-footer">
                    <RowActionIcons variant="inline">
                    <button type="button" onClick={() => openSplit(t)} className="btn-link" style={iconBtnStyle} title="Split (NLP)">
                      <IconSplit size={18} />
                    </button>
                    <button type="button" onClick={() => openDetail(t, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                      <IconEdit size={18} />
                    </button>
                    <button type="button" onClick={() => deleteDocument(t)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                      <IconDelete size={18} />
                    </button>
                    </RowActionIcons>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

export default Transaction;
